import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { API_LINK } from '../../apiLink/apiLink';

const LibraryFines = ({ onNavigate }) => {
    const [fines, setFines] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchFines();
    }, []);

    const fetchFines = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_LINK}/api/library/fines`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const result = await response.json();

            if (result.success) {
                setFines(result.data.fines || []);
            } else {
                setError(result.message || 'Failed to fetch fines');
            }
        } catch (err) {
            console.error('Error fetching fines:', err);
            setError('An error occurred while fetching fines');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0];
    };

    // Calculate aggregations
    const totalCollected = fines.reduce((sum, fine) => sum + parseFloat(fine.paid_amount || 0), 0);

    // Note: Pending fine calculation depends on structure. Let's assume total_fine - paid_amount is balance
    const totalPending = fines
        .filter(fine => fine.status === 'unpaid' || fine.status === 'partially_paid')
        .reduce((sum, fine) => {
            const balance = parseFloat(fine.total_fine || 0) - parseFloat(fine.paid_amount || 0);
            return sum + (balance > 0 ? balance : 0);
        }, 0);

    return (
        <Layout role="librarian" onNavigate={onNavigate} activePage="library-fines" pageTitle="Fine Management">
            <div style={{ padding: '20px' }}>
                <h2 style={{ color: '#333', marginBottom: '2rem' }}>💰 Fine Management</h2>

                {error && (
                    <div style={{ backgroundColor: '#ffeef0', color: '#e53935', padding: '15px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #ffccd5' }}>
                        <strong>❌ Error:</strong> {error}
                    </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
                    <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                        <h3 style={{ margin: 0, marginBottom: '1rem', color: '#666' }}>Total Fines Collected</h3>
                        <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#238548' }}>
                            ₹ {totalCollected.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </div>
                    </div>
                    <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                        <h3 style={{ margin: 0, marginBottom: '1rem', color: '#666' }}>Pending Fines</h3>
                        <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#f57c00' }}>
                            ₹ {totalPending.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </div>
                    </div>
                </div>

                <div style={{ marginTop: '2rem', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', padding: '20px' }}>
                    <h3 style={{ margin: 0, marginBottom: '1rem' }}>Latest Transactions</h3>
                    <div style={{ overflowX: 'auto' }}>
                        {loading ? (
                            <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Loading fines...</div>
                        ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left' }}>
                                        <th style={{ padding: '10px' }}>Fine ID</th>
                                        <th style={{ padding: '10px' }}>Member ID</th>
                                        <th style={{ padding: '10px' }}>Total Fine</th>
                                        <th style={{ padding: '10px' }}>Paid</th>
                                        <th style={{ padding: '10px' }}>Balance</th>
                                        <th style={{ padding: '10px' }}>Status</th>
                                        <th style={{ padding: '10px' }}>Date Issued</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {fines.length > 0 ? (
                                        fines.map(fine => (
                                            <tr key={fine.id} style={{ borderBottom: '1px solid #eee' }}>
                                                <td style={{ padding: '10px' }}>#{fine.id}</td>
                                                <td style={{ padding: '10px' }}>{fine.member_id}</td>
                                                <td style={{ padding: '10px', fontWeight: '500' }}>₹{parseFloat(fine.total_fine || 0).toFixed(2)}</td>
                                                <td style={{ padding: '10px', color: '#238548' }}>₹{parseFloat(fine.paid_amount || 0).toFixed(2)}</td>
                                                <td style={{ padding: '10px', color: '#f44336' }}>
                                                    ₹{Math.max(0, parseFloat(fine.total_fine || 0) - parseFloat(fine.paid_amount || 0)).toFixed(2)}
                                                </td>
                                                <td style={{ padding: '10px' }}>
                                                    <span style={{
                                                        backgroundColor: fine.status === 'paid' ? '#e8f5e9' : (fine.status === 'partially_paid' ? '#fff3e0' : '#ffebee'),
                                                        color: fine.status === 'paid' ? '#2e7d32' : (fine.status === 'partially_paid' ? '#ef6c00' : '#c62828'),
                                                        padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', textTransform: 'capitalize'
                                                    }}>
                                                        {fine.status ? fine.status.replace('_', ' ') : 'unpaid'}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '10px' }}>{formatDate(fine.created_at)}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="7" style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>No fine records found.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default LibraryFines;
